<template>
    <span v-if="isTimePicker">{{ facetName }}</span>
    <DatePicker
        v-model="dateRange"
        :test-id="facetId"
        :label="isTimePicker ? 'Select time' : 'Select date'"
        :disabled="isLoading"
        @update:model-value="onChangeDate"
        :clearable="true"
        :start-time="startTime"
        :enable-time-picker="isTimePicker"
        :is-range-picker="true"
        :show-preset="!isTimePicker"
        :show-multi-calendar="true"
        :week-start="weekStartDay"
    />
</template>

<script setup lang="ts">
import {
    ref, computed, inject, onMounted, watch
} from 'vue';
import { tz } from 'moment-timezone';
import DatePicker from '@/components/TeamAndSchedule/UI/organisms/datepicker/DatePicker.vue';
import { FacetItem } from '@/components/TeamAndSchedule/types/facetsTypes';
import { useSelectedViewFacet } from '@/components/TeamAndSchedule/composables/useSelectedViewFacet';
import { parseFacetsFromUrl } from '@/components/TeamAndSchedule/utils/facetUtils';
import { formatQueryParamDate } from '@agyletime/vue-3-common/src/utils/date';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import { calculateCurrentWeekDates } from '@/components/TeamAndSchedule/utils/dateUtils';
import { useWeekStartDay } from '@/components/TeamAndSchedule/composables/useWeekStartDay';

const emit = defineEmits(['change']);

const props = defineProps<{
    facetId: string;
    facetName: string;
    urlKey?: string;
    minDate?: Date;
    maxDate?: Date;
    isLoading: boolean;
    activeTable: string;
    startTime?: { hours?: number; minutes?: number; seconds?: number }[];
}>();

// Get the week start day from organisation settings
const { weekStartDay, fetchWeekStartDay } = useWeekStartDay();
const dateRange = ref();
const isTimePicker = computed(() => {
    return props?.facetId ? props.facetId.includes('tasksStartFacet') || props.facetId.includes('shiftStartFacet') : false;
});
const pagePreferences = inject<InjectedPagePreferences>('pagePreferences');
const resetFilters = inject('resetFilters', ref(false));
const userTimezone = inject('userTimezone', ref(tz.guess()));

type DateObject = {
    range: [string, string];
};

const isEmpty = computed(() => {
    return !props?.minDate && !props?.maxDate && !props.isLoading;
});

const onChangeDate = (_date: DateObject) => {
    // Handle clear action - when range is empty, emit a clear event
    if (!dateRange.value || !dateRange.value.range || !dateRange.value.range.length) {
        emit('change', {
            value: [],
            facetId: props.facetId,
            facetName: props.facetName,
            urlKey: `${props.urlKey}__$range`,
            boundaries: '',
            bdField: props.urlKey,
            shouldUpdate: true
        });
        return;
    }
    
    const [ minDateStr, maxDateStr ] = JSON.parse(JSON.stringify(dateRange.value.range));
    const startDate = formatQueryParamDate(minDateStr, userTimezone.value, 'start');
    const endDate = formatQueryParamDate(maxDateStr, userTimezone.value, 'end');
    
    emit('change', {
        value: dateRange.value.range,
        facetId: props.facetId,
        facetName: props.facetName,
        urlKey: `${props.urlKey}__$range`,
        boundaries: `${startDate}_${endDate}`,
        bdField: props.urlKey,
        shouldUpdate: true
    });
};

const onSelectedViewFacetChange = (value: unknown) => {
    const selectedViewValue = value as FacetItem;
    const currentWeekDates = calculateCurrentWeekDates(weekStartDay.value).split(',');
    const defaultRange = [ new Date(currentWeekDates[0]), new Date(currentWeekDates[1]) ];
    const selectedViewRanges = selectedViewValue
        ? selectedViewValue.value as [string, string]
        : defaultRange;
    dateRange.value = {
        range: selectedViewRanges.map((date: string | Date) => {
            return typeof date === 'string' ? date : date.toISOString();
        }) as [string, string]
    };
};

const isValidDateRange = (range: unknown): range is string[] => {
    return Array.isArray(range) && range.length === 2;
};
const loadDateRangeFromUrl = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('view.selected')) return;
    const facets = parseFacetsFromUrl();
    if (!facets) {
        await fetchWeekStartDay();
        const currentWeekDates = calculateCurrentWeekDates(weekStartDay.value).split(',');
        dateRange.value = { range: [ new Date(currentWeekDates[0]), new Date(currentWeekDates[1]) ] };
        return;
    }
    const selectedDateRange = facets[props.activeTable]?.[props.facetId]?.value;
    if (!isValidDateRange(selectedDateRange)) return;
    dateRange.value = { range: selectedDateRange.map((date: string) => new Date(date)) };
};
onMounted(async () => {
    loadDateRangeFromUrl();
});
watch([ () => props.minDate, () => props.maxDate ], ([ newMinDate, newMaxDate ], [ oldMinDate, oldMaxDate ]) => {
    if ((newMinDate !== oldMinDate || newMaxDate !== oldMaxDate) && !isEmpty.value) {
        loadDateRangeFromUrl();
    }
}, { immediate: true });

useSelectedViewFacet({
    pagePreferences,
    activeTable: props.activeTable,
    facetId: props.facetId,
    resetFilters,
    onSelectedViewFacetChange
});

</script>